import { MOBILE_OFFER_COUNTRIES } from '@numeo/types-main-mobile'
import { MobileOfferCapabilities, MobileOfferProvider } from '@numeo/types'
import { useMemo, useState } from 'react'
import { ChevronDown, Globe2, X } from 'lucide-react'
import { OfferButton, offerFieldClass } from './ui'
import { storeLabels } from './offerLabels'

export function availableOfferCountries(capabilities: MobileOfferCapabilities | null, providers: MobileOfferProvider[]): string[] {
    if (!capabilities || !providers.length || providers.some((provider) => !capabilities.stores[provider].configured || capabilities.stores[provider].error))
        return []
    return capabilities.stores[providers[0]].availableCountries
        .filter((country) => providers.every((provider) => capabilities.stores[provider].availableCountries.includes(country)))
        .sort()
}

export function CountryPicker({
    value,
    onChange,
    capabilities,
    providers,
    loading,
}: {
    value: string[]
    onChange: (countries: string[]) => void
    capabilities: MobileOfferCapabilities | null
    providers: MobileOfferProvider[]
    loading: boolean
}) {
    const [search, setSearch] = useState('')
    const available = useMemo(() => new Set(availableOfferCountries(capabilities, providers)), [capabilities, providers])
    const countries = useMemo(() => {
        const known = new Set(MOBILE_OFFER_COUNTRIES.map((country) => country.code))
        return [...MOBILE_OFFER_COUNTRIES, ...[...available].filter((code) => !known.has(code)).map((code) => ({ code, name: code, aliases: [] }))].sort(
            (a, b) => a.name.localeCompare(b.name)
        )
    }, [available])
    const matches = countries.filter((country) =>
        [country.name, country.code, ...country.aliases].some((text) => text.toLowerCase().includes(search.trim().toLowerCase()))
    )
    const selected = new Set(value)
    const unavailable = !loading && capabilities ? value.filter((country) => !available.has(country)) : []
    const toggle = (country: string) => onChange(selected.has(country) ? value.filter((item) => item !== country) : [...value, country].sort())
    return (
        <div className="space-y-2">
            <span className="text-sm font-medium" id="offer-country-label">
                Countries & regions
            </span>
            <details className="group rounded-lg border border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900">
                <summary
                    aria-labelledby="offer-country-label"
                    className="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-lg px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                >
                    <Globe2 size={17} className="text-slate-500 dark:text-slate-400" />
                    <span className="flex-1">
                        {value.length ? `${value.length} selected` : 'Choose countries'}
                        <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                            {loading ? 'Checking availability…' : `${available.size} available`}
                        </span>
                    </span>
                    <ChevronDown size={16} className="group-open:rotate-180" />
                </summary>
                <div className="space-y-3 border-t border-slate-200 p-3 dark:border-slate-700">
                    <input
                        type="search"
                        aria-label="Search countries"
                        placeholder="Search country or code (e.g. Uzbekistan, US, UK)"
                        className={offerFieldClass}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                    <div className="flex flex-wrap justify-between gap-2">
                        <OfferButton
                            variant="ghost"
                            disabled={loading || !available.size}
                            onClick={() => onChange([...new Set([...value, ...available])].sort())}
                        >
                            Select all available ({available.size})
                        </OfferButton>
                        <OfferButton variant="ghost" disabled={!value.length} onClick={() => onChange([])}>
                            Clear all
                        </OfferButton>
                    </div>
                    <div className="max-h-60 overflow-y-auto" role="group" aria-label="Available countries">
                        {matches.map((country) => {
                            const missing = providers
                                .filter((provider) => !capabilities?.stores[provider].availableCountries.includes(country.code))
                                .map((provider) => storeLabels[provider])
                            const disabled = loading || (!available.has(country.code) && !selected.has(country.code))
                            return (
                                <label
                                    key={country.code}
                                    className={`flex min-h-10 items-center gap-3 rounded-md px-2 py-2 text-sm ${disabled ? 'text-slate-400 dark:text-slate-500' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                >
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 accent-blue-600"
                                        checked={selected.has(country.code)}
                                        disabled={disabled}
                                        onChange={() => toggle(country.code)}
                                    />
                                    <span className="flex-1">
                                        {country.name}
                                        <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">{country.code}</span>
                                        {!loading && !available.has(country.code) && (
                                            <span className="block text-xs">
                                                {!providers.length ? 'Select a store first' : `Unavailable for ${missing.join(' & ') || 'the selected stores'}`}
                                            </span>
                                        )}
                                    </span>
                                </label>
                            )
                        })}
                        {!matches.length && <p className="p-3 text-sm text-slate-500 dark:text-slate-400">No matching countries.</p>}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        Select all includes every available country, not just search results. Both stores use their shared available countries.
                    </p>
                </div>
            </details>
            {value.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {value.slice(0, 4).map((code) => (
                        <button
                            type="button"
                            key={code}
                            onClick={() => toggle(code)}
                            aria-label={`Remove ${code}`}
                            className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700 focus-visible:ring-2 focus-visible:ring-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
                        >
                            {countries.find((country) => country.code === code)?.name || code}
                            <X size={12} />
                        </button>
                    ))}
                    {value.length > 4 && <span className="py-1 text-xs text-slate-500 dark:text-slate-400">+{value.length - 4} more</span>}
                </div>
            )}
            {unavailable.length > 0 && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    No longer available for this selection: {unavailable.join(', ')}. Remove these countries or change the selected stores. Nothing has been
                    removed automatically.
                </p>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400">
                An explicit country list is saved. Future countries and customer assignments are not added automatically.
            </p>
        </div>
    )
}
